import { StatusGreenIcon, StatusOrangeIcon, StatusRedIcon, getListCellValue } from '@isdd/idsk-ui-kit/index'
import { isAttributeRelation } from '@isdd/metais-common/componentHelpers'
import { SafeHtmlComponent, sanitizedHtml } from '@isdd/idsk-ui-kit/save-html-component/SafeHtmlComponent'
import { ATTRIBUTE_NAME, GET_ENUM, RELATION_TYPE } from '@isdd/metais-common/api/constants'
import { ConfigurationItemUi, ConfigurationItemUiAttributes, RoleParticipantUI } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { Attribute, CiType } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { FavoriteCiType } from '@isdd/metais-common/api/generated/user-config-swagger'
import { MetainformationColumns, getOwnerLabel } from '@isdd/metais-common/componentHelpers/ci/getCiDefaultMetaAttributes'
import { ENTITY_OBJEKT_EVIDENCIE, HTML_TYPE, MUK, OPERATOR_SEPARATOR, RELATION_FILTER_PREFIX } from '@isdd/metais-common/constants'
import { pairEnumsToEnumValues } from '@isdd/metais-common/index'
import { IListData } from '@isdd/metais-common/types/list'
import { CellContext, ColumnOrderState } from '@tanstack/react-table'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import styles from '@/components/ci-table/ciTable.module.scss'
import { JSX } from 'react'

interface ReducedAttributes {
    [technicalName: string]: Attribute
}

export interface ColumnsOutputDefinition {
    attributes?: ConfigurationItemUiAttributes
    metaAttributes?: {
        [key: string]: string
    }
    type?: string
    uuid?: string
    checked?: boolean
}

export interface IStoreColumnSelection {
    attributes: {
        name: string
        order: number
    }[]
    metaAttributes: {
        name: string
        order: number
    }[]
}

export const reduceAttributesByTechnicalName = (entityStructure: CiType | undefined) => {
    let allAttributes = [...(entityStructure?.attributes ?? [])]
    entityStructure?.attributeProfiles?.map((attribute) => {
        allAttributes = [...allAttributes, ...(attribute?.attributes ?? [])]
    })

    const groupBySlashCount = allAttributes.reduce((outputObject: ReducedAttributes, attribute) => {
        outputObject[attribute?.technicalName ?? ''] = attribute
        return outputObject
    }, {})

    return groupBySlashCount
}

export const mapTableData = (
    tableData: ConfigurationItemUi[] | undefined | void,
    reducedAttributes: ReducedAttributes,
    t: TFunction<'translation', undefined>,
    unitsData: EnumType | undefined,
    constraintsData?: (EnumType | undefined)[],
    uuidsToMatchedCiItemsMap?: Record<string, Record<string, ConfigurationItemUi | ConfigurationItemUi[]>> | undefined,
) => {
    return (tableData?.map((confItem: ConfigurationItemUi) => {
        const newAttributes: { [attributeName: string]: string | JSX.Element } = {}

        Object.keys(confItem?.attributes ?? {})?.map((attributeName: string) => {
            const isRelation = isAttributeRelation(attributeName)
            if (!isRelation) {
                const foundAttrWithTypes = reducedAttributes[attributeName]
                const newRowValue = pairEnumsToEnumValues({
                    attribute: foundAttrWithTypes,
                    ciItemData: confItem,
                    constraintsData: constraintsData ?? [],
                    t,
                    unitsData,
                    matchedAttributeNamesToCiItem: uuidsToMatchedCiItemsMap?.[confItem.uuid ?? ''] ?? {},
                    isFromTable: true,
                })
                newAttributes[attributeName] = newRowValue
            } else {
                newAttributes[attributeName] = confItem.attributes?.[attributeName] as string | JSX.Element
            }
        })

        return { ...confItem, attributes: newAttributes }
    }) ?? []) as ColumnsOutputDefinition[]
}

export const sortAndMergeCiColumns = (columnsList: FavoriteCiType) => {
    const columnsAttributes = columnsList?.attributes ?? []
    const columnsMetaAttributes = columnsList?.metaAttributes ?? []

    const mergedCiColumns = [...columnsAttributes, ...columnsMetaAttributes]
    //The column with the specified name('Gen_Profil_nazov') is always placed first, while the other columns are sorted according to order property
    mergedCiColumns?.sort((a, b) => {
        if (a.name === ATTRIBUTE_NAME.Gen_Profil_nazov) {
            return -1
        }
        if (b.name === ATTRIBUTE_NAME.Gen_Profil_nazov) {
            return 1
        }
        return (a?.order ?? 0) - (b?.order ?? 0)
    })

    return mergedCiColumns
}

export const getOrderCiColumns = (columnsList: FavoriteCiType, orderList: ColumnOrderState): IStoreColumnSelection => {
    const columnsAttributes = columnsList?.attributes ?? []
    const columnsMetaAttributes = columnsList?.metaAttributes ?? []

    const attributes: { order: number; name: string }[] = columnsAttributes.map((attribute) => {
        const order = orderList.indexOf(attribute.name || '')
        return { name: attribute.name || '', order }
    })

    const metaAttributes: { order: number; name: string }[] = columnsMetaAttributes.map((attribute) => {
        const order = orderList.indexOf(attribute.name || '')
        return { name: attribute.name || '', order }
    })
    return { attributes, metaAttributes }
}

export const reduceTableDataToObject = <T extends { uuid?: string }>(array: T[]): Record<string, T> => {
    return array.reduce<Record<string, T>>((result, item) => {
        if (item.uuid) {
            result[item.uuid] = item
        }
        return result
    }, {})
}

export const getOwnerInformation = (ownerGid: string, ownerList: RoleParticipantUI[] | undefined) => {
    const foundOwner = ownerList?.find((item) => item.gid === ownerGid)
    return foundOwner
}

export const isMetaAttribute = (metaTechnicalName: string) => {
    return (
        metaTechnicalName === MetainformationColumns.OWNER ||
        metaTechnicalName === MetainformationColumns.STATE ||
        metaTechnicalName === MetainformationColumns.CREATED_AT ||
        metaTechnicalName === MetainformationColumns.LAST_MODIFIED_AT
    )
}

export const getMetaAttributeName = (t: TFunction, metaTechnicalName: string, entityName?: string) => {
    if (metaTechnicalName === MetainformationColumns.OWNER) return getOwnerLabel(t, entityName)
    return t(`ciType.meta.${metaTechnicalName}`)
}

export const getRelationName = (t: TFunction, technicalName: string, entityName?: string) => {
    if (technicalName === `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Objekt_evidencie_ma_atribut_evidencie}`) {
        if (entityName === ENTITY_OBJEKT_EVIDENCIE) {
            return t(technicalName)
        } else {
            return t('navMenu.lists.evidenceObjects')
        }
    }
    return t(technicalName)
}

type GetColumnsFromApiCellContentProps = {
    index: number
    ctx: CellContext<ColumnsOutputDefinition, unknown>
    technicalName: string
    schemaAttributes: ReducedAttributes
    data: IListData
    isKRIS?: boolean
    linkToNewTab?: boolean
    baseHref?: string
    tooltip?: boolean
}

export const useGetColumnsFromApiCellContent = () => {
    const { t } = useTranslation()
    const location = useLocation()

    const getColumnsFromApiCellContent = ({
        index,
        ctx,
        technicalName,
        schemaAttributes,
        data,
        linkToNewTab,
        baseHref,
        tooltip,
    }: GetColumnsFromApiCellContentProps) => {
        const isFirstItem = index === 0
        const isInSchema = !!schemaAttributes[technicalName]?.name

        const isStatus = technicalName === MetainformationColumns.HEARTBEAT_STATUS
        const isMUK = technicalName === MUK
        const isState = technicalName === MetainformationColumns.STATE
        const isOwner = technicalName === MetainformationColumns.OWNER
        const isDate = technicalName === MetainformationColumns.LAST_MODIFIED_AT || technicalName === MetainformationColumns.CREATED_AT
        const isHTML = schemaAttributes?.[technicalName]?.type === HTML_TYPE
        const isRelation = isAttributeRelation(technicalName)

        switch (true) {
            case isFirstItem: {
                return (
                    <Link
                        to={baseHref ? `${baseHref}/${ctx?.row?.original?.uuid}` : './' + ctx?.row?.original?.uuid}
                        state={{ from: location }}
                        onClick={(e) => e.stopPropagation()}
                        target={linkToNewTab ? '_blank' : undefined}
                    >
                        {ctx?.getValue?.() as string}
                    </Link>
                )
            }
            case isStatus: {
                return (
                    <div className={styles.attributeGridRowBox}>
                        <img
                            src={
                                ctx.getValue() === GET_ENUM.LIVE
                                    ? StatusGreenIcon
                                    : ctx.getValue() === GET_ENUM.MAINTENANCE
                                      ? StatusOrangeIcon
                                      : StatusRedIcon
                            }
                            height={16}
                            alt=""
                        />
                        <>{t(`metaAttributes.state.${ctx.getValue()}`)}</>
                    </div>
                )
            }
            case isState: {
                return t(`metaAttributes.state.${ctx.getValue()}`)
            }
            case isMUK: {
                return t(`refRegisters.table.muk.${ctx.getValue()}`)
            }
            case isOwner: {
                return getOwnerInformation(ctx?.row?.original?.metaAttributes?.owner as string, data.gestorsData)?.configurationItemUi?.attributes?.[
                    ATTRIBUTE_NAME.Gen_Profil_nazov
                ] as string
            }
            case isDate: {
                return t('dateTime', { date: ctx.getValue() as string })
            }
            case isHTML: {
                return tooltip ? sanitizedHtml(ctx?.getValue?.() as string) : <SafeHtmlComponent dirtyHtml={ctx?.getValue?.() as string} />
            }
            case isInSchema: {
                return ctx.getValue() as string
            }
            case isRelation: {
                const values = ctx.getValue() as string[]
                return getListCellValue(values)
            }
            default: {
                return ''
            }
        }
    }

    return { getColumnsFromApiCellContent }
}
